import { Injectable, OnInit } from '@angular/core';
import { BehaviorSubject, Observable } from "rxjs";
import { GoogleLoginProvider, SocialAuthService, SocialUser } from '@abacritt/angularx-social-login';
import { StorageService } from '../storage/storage.service';
import * as jwt_decode from 'jwt-decode';
import { Router } from '@angular/router';
import { AppConstants } from 'src/app/constants/app-constants';
import { User } from 'src/app/model/user';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';

/**
 * UserService handles storing user information and session tokens throughout the application
 */
@Injectable({
  providedIn: 'root'
})
export class UserService {
  /** The current user's social construct, according to google */
  private _socialUser: SocialUser | null = null;

  /** Whether or not the user is authenticated */
  private _authenticatedSubject = new BehaviorSubject<boolean>(false);
  public authenticated$ = this._authenticatedSubject.asObservable();

  /** User model representation */
  private _userSubject = new BehaviorSubject<User | null>(null);
  public user$ = this._userSubject.asObservable();

  /** The token of the current user */
  private _tokenSubject = new BehaviorSubject<string>("");
  public token$ = this._tokenSubject.asObservable();

  /**
   * Default constructor for the user service which loads the token
   */
  constructor(
    private _storageService: StorageService,
    private _authService: SocialAuthService,
    private _router: Router,
    private _http: HttpClient,
  ) {
    // Listen to auth state changes
    this._authService.authState.subscribe(
      (user: SocialUser) => {
        this._socialUser = user;
        if (user == undefined) return;

        // Attempt to log the user in
        this.login(user.idToken);
      });


    // Get the old token if the user logs in
    this._authService.initState.subscribe(
      () => {
        this._storageService.loadItem("token").then(
          (token: string) => {
            // Attempt to log the user in
            this.login(token);
          });
      });
  }

  /**
   * Log the user in and initialize credentials
   */
  public login(token: string): void {
    // Don't try to log in with invalid tokens, or if the token hasn't changed when we last set it here
    if (token == undefined || token === "" || token === this._tokenSubject.value) return;

    // Decode the token into a readable object
    const jwt: any = jwt_decode.jwtDecode(token);

    if (jwt.exp * 1000 < Date.now()) {
      if (this._socialUser != undefined) {
        console.log("[LOG]: refreshing user token");

        // If the token has expired, refresh it only if the user is present
        this._authService.refreshAuthToken(GoogleLoginProvider.PROVIDER_ID);
      } else {
        // If the user is not present (refreshed the page, etc), log them out
        // TODO: maybe can refresh the auth token in a different way? need to get SocialUser back from given ID token
        this.logout();
      }
    } else {
      console.log("[LOG]: user logged in");

      // Token has not expired, so save the current token
      this._tokenSubject.next(token);

      // Save the token to the session storage
      this._storageService.setItem("token", token);
      this._authenticatedSubject.next(true);

      // Get the user's object to find permissions
      this._http.get<User>(environment.baseUrl + "/users/self").subscribe({
        next: (user: User) => {
          this._userSubject.next(user);
        },
        error: (err) => {
          console.log("[LOG]: error getting user object", err);
        }
      });
    }
  }

  /**
   * Log the user out
   */
  public logout(): void {
    console.log("[LOG]: user logging out");

    this._userSubject.next(null);
    this._tokenSubject.next("");

    // Remove the token from session storage
    this._storageService.removeItem("token");
    this._authenticatedSubject.next(false);

    // Log the user out of google
    this._authService.signOut()
      .then(() => console.log("[LOG]: logged user out of google"))
      .catch(() => console.log("[LOG]: user was not logged into google"));

    // Navigate the user to the login screen
    this._router.navigate([AppConstants.MENU_LOGIN]);
  }

  /**
   * Refresh the user's ID token
   */
  public refreshToken(): void {
    this._authService.refreshAuthToken(GoogleLoginProvider.PROVIDER_ID);
  }

  /** Getters */
  get user(): User | null {
    return this._userSubject.value;
  }

  get token(): string {
    return this._tokenSubject.value;
  }

  get authenticated(): boolean {
    return this._authenticatedSubject.value;
  }
}
